import { useCallback } from 'react';
import { useFeaturesContext } from '../providers/FeaturesProvider';

/**
 * useFeatures — access the subscription feature flags anywhere in the app.
 *
 * @returns {{
 *   features: object | null,
 *   isLoaded: boolean,
 *   hasFeature: (key: string) => boolean,
 *   hasIntegration: (slug: string) => boolean,
 * }}
 *
 * Usage:
 *   const { hasFeature, hasIntegration } = useFeatures();
 *   if (!hasFeature('campaign')) return null;
 *   if (hasIntegration('whatsapp')) { ... }
 */
export function useFeatures() {
  const context = useFeaturesContext();
  const features =
    context && typeof context === 'object' && 'features' in context
      ? context.features
      : context;

  /**
   * Returns `true` if the feature is enabled.
   * When `features` is null (still loading) it defaults to `true` so components
   * don't flash empty before the /me response lands.
   */
  const hasFeature = useCallback(
    (key) => {
      if (features === null) return true; // loading — optimistically show
      return features?.[key] === true;
    },
    [features],
  );

  /**
   * Returns `true` if the given integration slug is in `available_integrations`.
   * Defaults to `false` while loading (unlike hasFeature, integrations are additive).
   */
  const hasIntegration = useCallback(
    (slug) => {
      if (!features || !Array.isArray(features.available_integrations))
        return false;
      return features.available_integrations.includes(slug);
    },
    [features],
  );

  return {
    /** The raw features object — null while loading */
    features,
    /** True once the /me call has resolved and features is non-null */
    isLoaded: features !== null,
    hasFeature,
    hasIntegration,
  };
}
